import React, { useState } from 'react';
import { useData } from '../../store/DataContext';
import { LayoutDashboard, Users, FileText, FileEdit, CheckSquare, Clock, AlertCircle, FileSpreadsheet, Receipt, CreditCard, Undo2, List, BarChart3, Settings as SettingsIcon, Plus } from 'lucide-react';
import { BillingDashboard } from './BillingDashboard';
import { Customers } from './Customers';
import { ItemList } from './ItemList';
import { Invoices } from './Invoices';
import { NewInvoice } from './NewInvoice';
import { Payments } from './Payments';
import { Quotations } from './Quotations';
import { CreditNotes } from './CreditNotes';
import { Reports } from './Reports';
import { Settings } from './Settings';
export type BillingSection = 'dashboard' | 'customers' | 'new-invoice' | 'invoices-draft' | 'invoices-sent' | 'invoices-unpaid' | 'invoices-paid' | 'invoices-partial' | 'invoices-overdue' | 'quotations' | 'receipts' | 'payments' | 'credit-notes' | 'item-list' | 'reports' | 'settings';
export const BillingModule: React.FC = () => {
  const {
    currentUser
  } = useData();
  const [activeSection, setActiveSection] = useState<BillingSection>('dashboard');
  if (!currentUser || currentUser.role !== 'Administrator' && currentUser.role !== 'Reception') {
    return <div className="p-8 text-center text-red-600" data-id="element-949">
        You do not have permission to access the Billing Module.
      </div>;
  }
  const isAdmin = currentUser.role === 'Administrator';
  const navItems = [{
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard
  }, {
    id: 'customers',
    label: 'Customers',
    icon: Users
  }, {
    id: 'new-invoice',
    label: 'New Invoice',
    icon: Plus
  }, {
    id: 'invoices-draft',
    label: 'Draft Invoices',
    icon: FileEdit
  }, {
    id: 'invoices-sent',
    label: 'Sent Invoices',
    icon: FileText
  }, {
    id: 'invoices-unpaid',
    label: 'Unpaid Invoices',
    icon: AlertCircle
  }, {
    id: 'invoices-paid',
    label: 'Paid Invoices',
    icon: CheckSquare
  }, {
    id: 'invoices-partial',
    label: 'Partially Paid',
    icon: Clock
  }, {
    id: 'invoices-overdue',
    label: 'Overdue Invoices',
    icon: AlertCircle
  }, {
    id: 'quotations',
    label: 'Quotations',
    icon: FileSpreadsheet
  }, {
    id: 'receipts',
    label: 'Receipts',
    icon: Receipt
  }, {
    id: 'payments',
    label: 'Payments Received',
    icon: CreditCard
  }, {
    id: 'credit-notes',
    label: 'Credit Notes',
    icon: Undo2
  }, ...(isAdmin ? [{
    id: 'item-list',
    label: 'Item List',
    icon: List
  }] : []), {
    id: 'reports',
    label: 'Reports',
    icon: BarChart3
  }, ...(isAdmin ? [{
    id: 'settings',
    label: 'Settings',
    icon: SettingsIcon
  }] : [])];
  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return <BillingDashboard data-id="element-950" />;
      case 'customers':
        return <Customers data-id="element-951" />;
      case 'item-list':
        return <ItemList data-id="element-952" />;
      case 'new-invoice':
        return <NewInvoice onNavigate={setActiveSection} data-id="element-953" />;
      case 'invoices-draft':
      case 'invoices-sent':
      case 'invoices-unpaid':
      case 'invoices-paid':
      case 'invoices-partial':
      case 'invoices-overdue':
        return <Invoices filter={activeSection} onNavigate={setActiveSection} data-id="element-954" />;
      case 'payments':
      case 'receipts':
        return <Payments data-id="element-955" />;
      case 'quotations':
        return <Quotations onNavigate={setActiveSection} data-id="element-956" />;
      case 'credit-notes':
        return <CreditNotes onNavigate={setActiveSection} data-id="element-957" />;
      case 'reports':
        return <Reports data-id="element-958" />;
      case 'settings':
        return <Settings data-id="element-959" />;
      default:
        return <div className="p-8 text-center text-gray-500" data-id="element-960">
            <h2 className="text-2xl font-bold mb-4" data-id="element-961">Coming Soon</h2>
            <p data-id="element-962">This section ({activeSection}) is under construction.</p>
          </div>;
    }
  };
  return <div className="flex h-[calc(100vh-4rem)] bg-gray-50 -m-4 sm:-m-6 lg:-m-8" data-id="element-963">
      {/* Internal Sidebar */}
      <div className="w-64 bg-white border-r border-gray-200 flex flex-col overflow-y-auto" data-id="element-964">
        <div className="p-4 border-b border-gray-200" data-id="element-965">
          <h2 className="text-lg font-bold text-blue-800" data-id="element-966">BMED Billing</h2>
        </div>
        <nav className="flex-1 p-2 space-y-1" data-id="element-967">
          {navItems.map(item => {
          const Icon = item.icon;
          const isActive = activeSection === item.id;
          return <button key={item.id} onClick={() => setActiveSection(item.id as BillingSection)} className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'}`} data-id="element-968">
                <Icon className={`mr-3 h-5 w-5 flex-shrink-0 ${isActive ? 'text-blue-700' : 'text-gray-400'}`} data-id="element-969" />
                {item.label}
              </button>;
        })}
        </nav>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto p-6" data-id="element-970">{renderContent()}</div>
    </div>;
};